import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  CreateManualGiftpassOrderInput,
  GiftpassPrintStatus,
  MarkGiftpassOrderPaidInput,
  UpsertGiftpassPackageInput,
} from "@bednbody/types";
import { callEdgeFunction } from "@/lib/edge-functions";
import { supabase } from "@/lib/supabase";
import type { GiftpassOrder, GiftpassPackage } from "./giftpass-orders.model";

// Reads go through RLS (admin and superadmin read giftpass_orders, packages and
// passes — 0370, 0008). Every write is an admin Edge Function route (0373):
// the SQL functions behind them are service_role only and audited.

const ORDERS = ["giftpass-orders"] as const;
const PACKAGES = ["giftpass-packages"] as const;

export function useGiftpassOrders() {
  return useQuery({
    queryKey: ORDERS,
    queryFn: async (): Promise<GiftpassOrder[]> => {
      const { data, error } = await supabase
        .from("giftpass_orders")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(1000);
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 30_000,
  });
}

export interface OrderPass {
  id: string;
  code: string;
  status: string;
}

/** An order's passes: the batches it created (giftpass_batches.order_id, 0370). */
export function useOrderPasses(orderId: string | null) {
  return useQuery({
    queryKey: ["giftpass-order-passes", orderId],
    enabled: !!orderId,
    queryFn: async (): Promise<OrderPass[]> => {
      const { data: batches, error: batchError } = await supabase
        .from("giftpass_batches")
        .select("id")
        .eq("order_id", orderId!);
      if (batchError) throw batchError;
      const ids = (batches ?? []).map((b) => b.id);
      if (ids.length === 0) return [];
      // One order holds at most 5,000 passes (0373); PostgREST pages at 1,000.
      const passes: OrderPass[] = [];
      for (let from = 0; ; from += 1000) {
        const { data, error } = await supabase
          .from("giftpasses")
          .select("id, display_code, status")
          .in("batch_id", ids)
          .order("display_code")
          .range(from, from + 999);
        if (error) throw error;
        passes.push(
          ...(data ?? []).map((p) => ({ id: p.id, code: p.display_code, status: p.status })),
        );
        if (!data || data.length < 1000) break;
      }
      return passes;
    },
    staleTime: 60_000,
  });
}

export function useGiftpassPackages() {
  return useQuery({
    queryKey: PACKAGES,
    queryFn: async (): Promise<GiftpassPackage[]> => {
      const { data, error } = await supabase
        .from("giftpass_packages")
        .select("*")
        .order("sort_order")
        .order("price");
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 60_000,
  });
}

export function useGiftpassReferrals() {
  return useQuery({
    queryKey: ["giftpass-referrals"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("giftpass_referrals")
        .select("id, name, active")
        .order("sort_order");
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 5 * 60_000,
  });
}

function useOrderWrite<TInput, TResult>(write: (input: TInput) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: write,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ORDERS });
      queryClient.invalidateQueries({ queryKey: ["giftpass-order-passes"] });
      queryClient.invalidateQueries({ queryKey: ["giftpass-batches"] });
    },
  });
}

export interface AdminOrderResult {
  orderId: string;
  orderCode: string;
  status: string;
}

export function useCreateManualOrder() {
  return useOrderWrite((input: CreateManualGiftpassOrderInput) =>
    callEdgeFunction<AdminOrderResult>("admin/giftpass-orders", { data: input }),
  );
}

export function useMarkOrderPaid() {
  return useOrderWrite((input: { orderId: string } & MarkGiftpassOrderPaidInput) =>
    callEdgeFunction<AdminOrderResult>(`admin/giftpass-orders/${input.orderId}/mark-paid`, {
      data: { method: input.method, reference: input.reference, proofPath: input.proofPath },
    }),
  );
}

export function useCancelOrder() {
  return useOrderWrite((orderId: string) =>
    callEdgeFunction<AdminOrderResult>(`admin/giftpass-orders/${orderId}/cancel`, { data: {} }),
  );
}

export function useSetPrintStatus() {
  return useOrderWrite((input: { orderIds: string[]; printStatus: GiftpassPrintStatus }) =>
    // One call per order: each step is its own audited row (0373).
    Promise.all(
      input.orderIds.map((id) =>
        callEdgeFunction<AdminOrderResult>(`admin/giftpass-orders/${id}/print-status`, {
          data: { printStatus: input.printStatus },
        }),
      ),
    ),
  );
}

export function useSavePackage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string | null } & UpsertGiftpassPackageInput) => {
      const { id, ...data } = input;
      return id
        ? callEdgeFunction<GiftpassPackage>(`admin/giftpass-packages/${id}`, {
            method: "PATCH",
            data,
          })
        : callEdgeFunction<GiftpassPackage>("admin/giftpass-packages", { data });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PACKAGES }),
  });
}

// ── Proof of payment (0375) ────────────────────────────────────────────────

const PROOF_BUCKET = "giftpass-payment-proofs";
const PROOF_TYPES: Record<string, string> = {
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/webp": "webp",
  "application/pdf": "pdf",
};
export const PROOF_ACCEPT = Object.keys(PROOF_TYPES).join(",");
const PROOF_MAX_BYTES = 10 * 1024 * 1024;

/**
 * Uploads a proof image to the private bucket (Superadmin only, 0375) and
 * returns its path: `<uploader>/<random>.<ext>`. Nothing ever replaces it.
 */
export async function uploadProof(file: File): Promise<string> {
  const ext = PROOF_TYPES[file.type];
  if (!ext) throw new Error("Attach a photo (JPG, PNG, WebP) or a PDF.");
  if (file.size > PROOF_MAX_BYTES)
    throw new Error("The proof is over 10 MB. Attach a smaller file.");
  const { data: session } = await supabase.auth.getSession();
  const uid = session.session?.user.id;
  if (!uid) throw new Error("Not signed in. Sign in again.");
  const path = `${uid}/${crypto.randomUUID()}.${ext}`;
  const { error } = await supabase.storage
    .from(PROOF_BUCKET)
    .upload(path, file, { contentType: file.type, upsert: false });
  if (error) throw error;
  return path;
}

/** A short-lived link to view an order's proof. */
export function useProofUrl(path: string | null) {
  return useQuery({
    queryKey: ["giftpass-proof", path],
    enabled: !!path,
    queryFn: async () => {
      const { data, error } = await supabase.storage.from(PROOF_BUCKET).createSignedUrl(path!, 300);
      if (error) throw error;
      return data.signedUrl;
    },
    staleTime: 4 * 60_000,
  });
}

/** G5 — email a paid order's codes to its buyer (again). Refused until email is set up. */
export function useEmailCodes() {
  return useOrderWrite((orderId: string) =>
    callEdgeFunction<{ orderId: string; sent: number }>(`admin/giftpass-orders/${orderId}/email`, {
      data: {},
    }),
  );
}

// ── Sellers are partners (0385) ──────────────────────────────────────────────

export interface SellerPartner {
  id: string;
  name: string;
  role: "therapist" | "rider";
  code: string;
}

/** Therapists and riders with their partner code, for the seller picker. */
export function useSellerPartners() {
  return useQuery({
    queryKey: ["giftpass-seller-partners"],
    queryFn: async (): Promise<SellerPartner[]> => {
      const { data, error } = await supabase
        .from("staff")
        .select("id, full_name, role, therapist_code, rider_code")
        .in("role", ["therapist", "rider"])
        .order("full_name");
      if (error) throw error;
      return (data ?? []).flatMap((s) => {
        // By role: a rider who once held a therapist code shows his MR- code (0386).
        const code = s.role === "rider" ? s.rider_code : s.therapist_code;
        if (!code || (s.role !== "therapist" && s.role !== "rider")) return [];
        return [{ id: s.id, name: s.full_name, role: s.role, code }];
      });
    },
    staleTime: 5 * 60_000,
  });
}

/** Super Admin only, audited (set_giftpass_order_seller, 0385). */
export function useSetOrderSeller() {
  return useOrderWrite(async (input: { orderId: string; staffId: string | null }) => {
    const { error } = await supabase.rpc("set_giftpass_order_seller", {
      p_order_id: input.orderId,
      p_staff_id: input.staffId as string,
    });
    if (error) throw error;
  });
}
